import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronRight, BookOpen,
  FileText, Cookie, BadgeCheck, MapPin, Mail, Hash
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

function VisaMark() {
  return (
    <svg viewBox="0 0 48 16" width="38" height="13" aria-hidden="true">
      <path fill="#1A1F71" d="M20.9.4h-4L14.2 15.6h4l2.7-15.2zM33.7.7c-.8-.3-2-.6-3.6-.6-4 0-6.8 2-6.8 4.9 0 2.2 2.1 3.3 3.7 4 1.6.7 2.2 1.2 2.2 1.9 0 1-1.3 1.5-2.5 1.5-1.7 0-2.6-.2-4-.8l-.5-.3-.6 3.4c1 .4 2.7.8 4.6.8 4.3 0 7-2 7-5.1 0-1.7-1.1-3-3.5-4-1.5-.7-2.4-1.2-2.4-1.9 0-.6.7-1.3 2.4-1.3 1.4 0 2.4.3 3.2.6l.4.2.6-3.3zM43.8.4h-3.1c-1 0-1.7.3-2.1 1.2l-6 13.9h4.3l.9-2.3h5.3l.5 2.3h3.8L43.8.4zm-5 9.6 1.6-4.1c0 .1.3-.8.5-1.4l.3 1.2 .9 4.3h-3.3zM11.4.4 7.5 10.7 7.1 8.6C6.4 6.2 4.1 3.6 1.6 2.3l3.6 13.3h4.3L16 .4h-4.6z"/>
    </svg>
  );
}

function MastercardMark() {
  return (
    <svg viewBox="0 0 48 30" width="34" height="21" aria-hidden="true">
      <circle cx="18" cy="15" r="12" fill="#eb001b" />
      <circle cx="30" cy="15" r="12" fill="#f79e1b" />
      <path fill="#ff5f00" d="M24 5.5a12 12 0 0 1 0 19 12 12 0 0 1 0-19z" />
    </svg>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const handleVerifyClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      document.getElementById('verify-certificate')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#verify-certificate');
      setTimeout(() => {
        document.getElementById('verify-certificate')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand-section">
            <Link
              to="#"
              className="footer-brand"
              onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              <div className="footer-logo">
                <img src="/gigaone-favicon.png" alt="Gigaone" />
              </div>
              <span className="footer-title">Gigaone</span>
            </Link>
            <p className="footer-description">
              {t('footer.description')}
            </p>
            <div className="footer-company-info">
              <span className="footer-company-name">Glacio Ltd</span>
              <span className="footer-company-item">
                <MapPin size={13} />
                Stavrodromiou, 69, Flat/Office 201, 6045, Larnaca, Cyprus
              </span>
              <span className="footer-company-item">
                <Hash size={13} />
                Organization No. 677497
              </span>
              <a className="footer-company-item footer-company-email" href="mailto:contact@gigaone.tech">
                <Mail size={13} />
                contact@gigaone.tech
              </a>
              <div className="footer-payment-badges" aria-label="Accepted payment methods">
                <span className="footer-payment-badge">
                  <VisaMark />
                </span>
                <span className="footer-payment-badge">
                  <MastercardMark />
                </span>
              </div>
            </div>
            {/* <div className="footer-social">
              <button className="social-link" aria-label="Discord" onClick={() => {}}>
                <MessageCircle size={18} />
              </button>
              <button className="social-link" aria-label="Facebook" onClick={() => {}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </button>
            </div> */}
          </div>

          <div className="footer-column">
            <h4 className="footer-column-title">{t('footer.columns.product')}</h4>
            <ul className="footer-column-links">
              <li><Link to="/products"><ChevronRight size={14} /> {t('footer.links.features')}</Link></li>
              <li><Link to="/certificates"><ChevronRight size={14} /> {t('footer.links.smartContracts')}</Link></li>
              <li><a href="#verify-certificate" onClick={handleVerifyClick}><BadgeCheck size={14} /> {t('nav.verify')}</a></li>
            </ul>
          </div>

          <div className="footer-column">
            <h4 className="footer-column-title">{t('footer.columns.legal')}</h4>
            <ul className="footer-column-links">
              <li><Link to="/privacy"><FileText size={14} /> {t('footer.links.privacyPolicy')}</Link></li>
              <li><Link to="/terms"><FileText size={14} /> {t('footer.links.terms')}</Link></li>
              <li><Link to="/cookies"><Cookie size={14} /> {t('footer.links.cookies')}</Link></li>
            </ul>
          </div>

          <div className="footer-column">
            <h4 className="footer-column-title">{t('footer.columns.support')}</h4>
            <ul className="footer-column-links">
              <li><Link to="/blogs"><BookOpen size={14} /> {t('footer.links.blog')}</Link></li>
              <li><Link to="/contact"><ChevronRight size={14} /> {t('footer.links.contactUs')}</Link></li>
              <li><Link to="/troubleshoot"><ChevronRight size={14} /> {t('footer.links.troubleshoot')}</Link></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2026. {t('footer.rights')}</p>
        </div>
      </div>
    </footer>
  );
}
